import {memo, useMemo} from 'react';
import {Typography} from 'components/Typography';
import Link from 'components/CustomRoutingLink';
import {getFooterChatList, getFooterSocialList} from 'modules/footer/helpers';
import {APP_LIST} from 'modules/footer/components/Footer/constants';
import style from './style.module.scss';
import classNames from 'classnames';
import {useAppSelector} from 'core/redux/hooks/useAppSelector';
import {selectFooterSocialNetworks} from 'modules/footer/slice';

interface ISocialLink {
    icon: JSX.Element;
    path?: string;
    code?: string;
}

interface ISocialListItemProps {
    title: string;
    linkList: ISocialLink[];
    className?: string;
    adult?: boolean;
}

const SocialLinkItem = memo(({icon, path}: ISocialLink & {index: number}) => (
    <div className={style.link}>
        {path ? (
            <Link href={path} target={'_blank'}>
                <div className={style.icon}>{icon}</div>
            </Link>
        ) : (
            <div className={style.icon}>{icon}</div>
        )}
    </div>
));

const SocialListItem = memo<ISocialListItemProps>(({title, linkList, className, adult = false}) => (
    <div className={classNames(style.socialListItem, className)}>
        <Typography className={style.title} variant={'label-14-r'} element={'div'}>
            {title}
            {adult && <span className={style.title__adult}>18+</span>}
        </Typography>
        <div className={style.links}>
            {linkList.map((link, index) => (
                <SocialLinkItem key={`${link.code}-${index}`} index={index} {...link} />
            ))}
        </div>
    </div>
));

const AppList = memo<ISocialListItemProps>(({title, linkList, className}) => (
    <div className={classNames(style.socialListItem, className)}>
        <Typography className={style.title} variant={'label-14-r'}>
            {title}
        </Typography>
        <div className={classNames(style.links, style.apps)}>
            {linkList.map((link, index) => (
                <SocialLinkItem key={`${link.code}-${index}`} index={index} {...link} />
            ))}
        </div>
        <div className={classNames(style.appQr)}>
            <img alt={'QR код приложения'} src={'/images/footer-app-qr.svg'} />
            <Typography variant={'label-12-r'} color={'gray60'}>
                наведите камеру на QR-код
                <br />
                для установки приложения
            </Typography>
        </div>
    </div>
));

interface ISocialListSectionProps {
    linkList?: ISocialLink[];
    title?: string;
    className?: string;
    adult?: boolean;
}

const SocialListSection = memo(({linkList, title, className, adult = false}: ISocialListSectionProps) => {
    if (!linkList || !title) {
        return null;
    }

    return <SocialListItem linkList={linkList} title={title} className={className} adult={adult} />;
});

export const SocialList = memo(() => {
    const socialNets = useAppSelector(selectFooterSocialNetworks);

    const {footerSocialList, footerChatList} = useMemo(
        () => ({
            footerSocialList: socialNets ? getFooterSocialList(socialNets) : null,
            footerChatList: socialNets ? getFooterChatList(socialNets) : null,
        }),
        [socialNets],
    );

    return (
        <>
            <div className={style.socialBlock}>
                <SocialListSection
                    linkList={footerSocialList?.linkList}
                    title={footerSocialList?.title}
                    className={style.socials}
                    adult={true}
                />

                <SocialListSection
                    linkList={footerChatList?.linkList}
                    title={footerChatList?.title}
                    className={style.chat}
                />
            </div>
            <AppList {...APP_LIST()} />
        </>
    );
});
